import { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Button,
  useDisclosure,
  Stack,
  Image,
  Container,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { logoTransparent } from '../lib/storage';

interface NavBarProps {
  onNavigate: (section: string) => void;
}

const NavBar: React.FC<NavBarProps> = ({ onNavigate }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Início', section: 'home' },
    { label: 'Sobre', section: 'about' },
    { label: 'Áreas de Atuação', section: 'practice-areas' },
    { label: 'Equipe', section: 'team' },
    { label: 'Depoimentos', section: 'testimonials' },
    { label: 'Unidades', section: 'locations' },
    { label: 'Contato', section: 'contact' },
  ];

  const handleNavClick = (section: string) => {
    onNavigate(section);
    onClose();
  };

  return (
    <Box
      as="nav"
      position="fixed"
      top={0}
      left={0}
      right={0}
      zIndex={1000}
      bg={isScrolled ? 'rgba(1, 3, 2, 0.95)' : 'transparent'}
      backdropFilter={isScrolled ? 'blur(10px)' : 'none'}
      boxShadow={isScrolled ? 'lg' : 'none'}
      transition="all 0.3s ease"
    >
      <Container maxW="1400px">
        <Flex
          h={isScrolled ? { base: 16, md: 20 } : { base: 24, md: 36 }}
          alignItems="center"
          justifyContent="space-between"
          py={4}
        >
          <Box
            cursor="pointer"
            onClick={() => handleNavClick('home')}
            transition="transform 0.3s ease"
            _hover={{ transform: 'scale(1.05)' }}
          >
            <Image
              src={logoTransparent}
              alt="Mendes Assessoria Jurídica - Logotipo do escritório de advocacia"
              w={isScrolled ? { base: '56px', md: '104px' } : { base: '92px', md: '190px' }}
              h={isScrolled ? { base: '56px', md: '104px' } : { base: '92px', md: '190px' }}
              objectFit="contain"
            />
          </Box>

          <HStack
            as="ul"
            spacing={5}
            display={{ base: 'none', lg: 'flex' }}
            listStyleType="none"
          >
            {navItems.map((item) => (
              <Box as="li" key={item.section}>
                <Button
                  variant="ghost"
                  color="white"
                  fontWeight={400}
                  fontSize="sm"
                  _hover={{
                    color: 'alabasterGrey',
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.3s ease"
                  onClick={() => handleNavClick(item.section)}
                >
                  {item.label}
                </Button>
              </Box>
            ))}
          </HStack>

          <IconButton
            size="md"
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            aria-label="Abrir menu de navegação"
            display={{ lg: 'none' }}
            onClick={isOpen ? onClose : onOpen}
            bg={isScrolled ? 'whiteAlpha.200' : 'blackAlpha.400'}
            color="white"
            _hover={{
              bg: isScrolled ? 'whiteAlpha.300' : 'blackAlpha.500',
            }}
          />
        </Flex>

        {isOpen && (
          <Box
            pb={4}
            display={{ lg: 'none' }}
            bg="yaleBlue"
            borderRadius="md"
            mt={2}
          >
            <Stack as="ul" spacing={4} listStyleType="none">
              {navItems.map((item) => (
                <Box as="li" key={item.section}>
                  <Button
                    variant="ghost"
                    w="full"
                    color="white"
                    fontWeight={500}
                    justifyContent="flex-start"
                    onClick={() => handleNavClick(item.section)}
                    _hover={{
                      bg: 'whiteAlpha.200',
                      transform: 'translateX(8px)',
                    }}
                    transition="all 0.3s ease"
                  >
                    {item.label}
                  </Button>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default NavBar;
